<script setup lang="ts">
import { ref } from 'vue';
import { Layers, Plus } from 'lucide-vue-next';
import DButton from '../../design/components/DButton.vue';
import ProjectIcon from '../../design/components/ProjectIcon.vue';
import NewProjectDialog from '../workspace/NewProjectDialog.vue';
import BrandBar from './BrandBar.vue';
import OrgSwitcher from './OrgSwitcher.vue';
import { navTarget, PRIMARY_NAV, unreadLabel } from './navigation.ts';
import { useInboxStore } from '../../app/stores/inbox.ts';
import { useWorkspaceStore } from '../../app/stores/workspace.ts';

const emit = defineEmits<{ navigate: [] }>();
const workspace = useWorkspaceStore();
const inbox = useInboxStore();
const creating = ref(false);
</script>

<template>
  <BrandBar @navigate="emit('navigate')" />
  <OrgSwitcher @navigate="emit('navigate')" />

  <nav class="sidebar__section" aria-label="Main">
    <RouterLink
      v-for="item in PRIMARY_NAV"
      :key="item.to"
      :to="navTarget(item, workspace.currentSlug)"
      class="sidebar__link"
      :class="{ 'sidebar__link--disabled': !item.enabled }"
      active-class=""
      exact-active-class="sidebar__link--active"
      :title="item.label"
      :aria-disabled="!item.enabled || undefined"
      :tabindex="item.enabled ? undefined : -1"
      @click="emit('navigate')"
    >
      <span class="nav-icon">
        <component :is="item.icon" :size="16" :stroke-width="1.75" aria-hidden="true" />
        <span
          v-if="item.counts && inbox.countFor(workspace.currentSlug)"
          class="nav-icon__dot"
          aria-hidden="true"
        />
      </span>
      <span class="sidebar__label spacer">{{ item.label }}</span>
      <span
        v-if="item.counts && inbox.countFor(workspace.currentSlug)"
        class="count-badge sidebar__label"
        :aria-label="unreadLabel(inbox.countFor(workspace.currentSlug))"
        >{{ Math.min(inbox.countFor(workspace.currentSlug), 99) }}</span
      >
    </RouterLink>
  </nav>

  <div v-if="workspace.current" class="sidebar__section">
    <div class="sidebar__heading-row">
      <p class="sidebar__heading">Projects</p>
      <DButton
        v-if="workspace.current.role !== 'guest'"
        class="sidebar__label"
        variant="ghost"
        size="sm"
        icon
        aria-label="New project"
        title="New project"
        @click="creating = true"
      >
        <Plus :size="14" :stroke-width="2" />
      </DButton>
    </div>
    <RouterLink
      v-if="workspace.projects.length > 1"
      :to="{ name: 'multi-work', params: { org: workspace.current.slug } }"
      class="sidebar__link"
      active-class="sidebar__link--active"
      title="Across projects"
      @click="emit('navigate')"
    >
      <Layers :size="16" :stroke-width="1.75" aria-hidden="true" />
      <span class="sidebar__label">Across projects</span>
    </RouterLink>
    <RouterLink
      v-for="project in workspace.projects"
      :key="project.id"
      :to="{ name: 'project', params: { org: workspace.current.slug, key: project.key } }"
      class="sidebar__link"
      active-class="sidebar__link--active"
      :title="project.name"
      @click="emit('navigate')"
    >
      <ProjectIcon :project-key="project.key" :color="project.color" />
      <span class="sidebar__label truncate">{{ project.name }}</span>
    </RouterLink>
    <p v-if="!workspace.projects.length" class="sidebar__heading sidebar__label">No projects yet</p>
    <NewProjectDialog v-model:open="creating" :org="workspace.current.slug" />
  </div>
</template>
